import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core';
import { z } from 'zod';

const AuthorizeViaCodeRequestDto = z.object({ code: z.string().min(1), lastName: z.string().min(1) });
const OkResponseDto = z.object({ success: z.literal(true) });
const AuthorizeViaCodeResponseDto = z.object({ key: z.string(), reservationId: z.string() });
const ErrorResponseDto = z.object({ success: z.literal(false), message: z.unknown().optional() });
const AuthorizeViaCodeErrorDto = z.union([
  z.array(z.object({ code: z.string(), message: z.string(), path: z.array(z.string()) })),
  z.object({ code: z.literal('reservation-not-found') }),
  z.object({ code: z.literal('name-mismatch') }),
]);
const CancelReservationRequestDto = z.object({ reason: z.string(), description: z.string().optional() });
const UpdateBillingAddressDto = z.discriminatedUnion('travelPurpose', [
  z.object({
    addressLine1: z.string().min(2),
    addressLine2: z.string().optional(),
    postalCode: z.string().min(2),
    city: z.string().min(2),
    countryCode: z.string().min(2),
    regionCode: z.string().optional(),
    travelPurpose: z.literal('leisure'),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    taxId: z.string().optional(),
    personalTaxId: z.string().optional(),
  }),
  z.object({
    addressLine1: z.string().min(2),
    addressLine2: z.string().optional(),
    postalCode: z.string().min(2),
    city: z.string().min(2),
    countryCode: z.string().min(2),
    regionCode: z.string().optional(),
    travelPurpose: z.literal('business'),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    companyName: z.string().min(2),
    taxId: z.string().optional(),
    additionalTaxId: z.string().optional(),
    personalTaxId: z.string().optional(),
  }),
]);
const UpdateBillingAddressErrorDto = z.array(
  z.union([
    z.object({ code: z.string(), message: z.string(), path: z.array(z.string()) }),
    z.object({
      code: z.literal('custom'),
      message: z.string(),
      path: z.array(z.string()),
      params: z.object({
        customErrorCode: z.enum([
          'missingCodiceFiscale',
          'invalidCodiceFiscale',
          'missingVatId',
          'invalidVatId',
          'missingPartitaIva',
          'invalidPartitaIva',
          'invalidCodiceDestinatario',
          'invalidNif',
        ]),
      }),
    }),
  ]),
);
const GetIdVerificationInfoResponseDto = z.object({
  reservationId: z.string(),
  requiresIdVerification: z.boolean(),
  guests: z.array(
    z.object({
      id: z.number(),
      firstName: z.string(),
      lastName: z.string().optional(),
      requiresIdVerification: z.boolean(),
      incodeExternalId: z.string(),
    }),
  ),
});
const PostNpsScoreDto = z.object({ score: z.number().gte(0).lte(10) });
const PostFeedbackDto = z.object({
  feedbackItems: z
    .array(z.object({ value: z.string(), children: z.array(z.object({ value: z.string() })).optional() }))
    .min(1),
  type: z.enum(['positive', 'improvement']),
});
const PostAdditionalInfoDto = z.object({ additionalInfo: z.string() });
const BreakfastSelectionDto = z.array(
  z.object({
    date: z.string(),
    selection: z.union([
      z.object({ type: z.literal('menu'), modality: z.literal('individual'), data: z.record(z.record(z.number())) }),
      z.object({ type: z.literal('menu'), modality: z.literal('group'), data: z.record(z.number()) }),
      z.object({
        type: z.literal('partner'),
        modality: z.literal('individual'),
        data: z.record(z.object({ partnerId: z.string().optional(), breakfastCount: z.number().gte(0) })),
      }),
      z.object({
        type: z.literal('partner'),
        modality: z.literal('group'),
        data: z.object({ partnerId: z.string().optional(), breakfastCount: z.number().gte(0) }),
      }),
    ]),
  }),
);
const TimeOptionsDto = z.object({
  checkInTimes: z.array(
    z.object({ time: z.string(), type: z.enum(['standard', 'guaranteed', 'not-guaranteed', 'member-benefit']) }),
  ),
  checkOutTimes: z.array(
    z.object({ time: z.string(), type: z.enum(['standard', 'guaranteed', 'not-guaranteed', 'member-benefit']) }),
  ),
});
const ApiKeyAuthGuard = z.object({}).partial();
const AddressDto = z.object({
  addressLine1: z.string().min(2),
  addressLine2: z.string().optional(),
  postalCode: z.string().min(2),
  city: z.string().min(2),
  countryCode: z.string().min(2),
  regionCode: z.string().optional(),
});
const FeedbackItemDto = z.object({ value: z.string() });
const AccessInformationV3Dto = z.union([
  z.discriminatedUnion('hasAccess', [
    z.object({
      type: z.literal('pinCode'),
      hasAccess: z.literal(true),
      unitName: z.string(),
      pinCodes: z.array(z.object({ id: z.string(), code: z.string() })),
      actions: z.array(z.object({ type: z.literal('manualReceptionIdCheck') })),
    }),
    z.object({
      type: z.literal('pinCode'),
      hasAccess: z.literal(false),
      reasons: z.array(
        z.enum([
          'before-arrival',
          'reservation-not-paid',
          'unpaid-city-tax',
          'online-checkin-not-done',
          'no-unit-assigned',
          'pending-delayed-ibe-payment',
          'reservation-not-active',
        ]),
      ),
      actions: z.array(
        z.object({
          type: z.literal('payment'),
          paymentType: z.enum(['cityTaxPayment', 'delayedIbePayment']),
          url: z.string().url(),
          amount: z.number(),
          currency: z.string(),
          expiresAt: z.string().optional(),
        }),
      ),
    }),
  ]),
  z.discriminatedUnion('hasAccess', [
    z.object({
      type: z.literal('keycard'),
      hasAccess: z.literal(true),
      unitName: z.string(),
      actions: z.array(z.object({ type: z.literal('manualReceptionIdCheck') })),
    }),
    z.object({
      type: z.literal('keycard'),
      hasAccess: z.literal(false),
      reasons: z.array(
        z.enum([
          'before-arrival',
          'reservation-not-paid',
          'unpaid-city-tax',
          'online-checkin-not-done',
          'no-unit-assigned',
          'pending-delayed-ibe-payment',
          'reservation-not-active',
          'keycard-not-handed-out',
        ]),
      ),
      actions: z.array(
        z.object({
          type: z.literal('payment'),
          paymentType: z.enum(['cityTaxPayment', 'delayedIbePayment']),
          url: z.string().url(),
          amount: z.number(),
          currency: z.string(),
          expiresAt: z.string().optional(),
        }),
      ),
    }),
  ]),
]);

export const schemas = {
  AuthorizeViaCodeRequestDto,
  OkResponseDto,
  AuthorizeViaCodeResponseDto,
  ErrorResponseDto,
  AuthorizeViaCodeErrorDto,
  CancelReservationRequestDto,
  UpdateBillingAddressDto,
  UpdateBillingAddressErrorDto,
  GetIdVerificationInfoResponseDto,
  PostNpsScoreDto,
  PostFeedbackDto,
  PostAdditionalInfoDto,
  BreakfastSelectionDto,
  TimeOptionsDto,
  ApiKeyAuthGuard,
  AddressDto,
  FeedbackItemDto,
  AccessInformationV3Dto,
};
export type AuthorizeViaCodeRequestDto = z.infer<typeof AuthorizeViaCodeRequestDto>;
export type OkResponseDto = z.infer<typeof OkResponseDto>;
export type AuthorizeViaCodeResponseDto = z.infer<typeof AuthorizeViaCodeResponseDto>;
export type ErrorResponseDto = z.infer<typeof ErrorResponseDto>;
export type AuthorizeViaCodeErrorDto = z.infer<typeof AuthorizeViaCodeErrorDto>;
export type CancelReservationRequestDto = z.infer<typeof CancelReservationRequestDto>;
export type UpdateBillingAddressDto = z.infer<typeof UpdateBillingAddressDto>;
export type UpdateBillingAddressErrorDto = z.infer<typeof UpdateBillingAddressErrorDto>;
export type GetIdVerificationInfoResponseDto = z.infer<typeof GetIdVerificationInfoResponseDto>;
export type PostNpsScoreDto = z.infer<typeof PostNpsScoreDto>;
export type PostFeedbackDto = z.infer<typeof PostFeedbackDto>;
export type PostAdditionalInfoDto = z.infer<typeof PostAdditionalInfoDto>;
export type BreakfastSelectionDto = z.infer<typeof BreakfastSelectionDto>;
export type TimeOptionsDto = z.infer<typeof TimeOptionsDto>;
export type ApiKeyAuthGuard = z.infer<typeof ApiKeyAuthGuard>;
export type AddressDto = z.infer<typeof AddressDto>;
export type FeedbackItemDto = z.infer<typeof FeedbackItemDto>;
export type AccessInformationV3Dto = z.infer<typeof AccessInformationV3Dto>;

const endpoints = makeApi([
  {
    method: 'patch',
    path: '/reservation/v2/:reservationId/billing-address',
    alias: 'updateBillingAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateBillingAddressDto,
      },
      {
        name: 'reservationId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OkResponseDto,
    errors: [
      {
        status: 400,
        schema: ErrorResponseDto.and(z.object({ message: UpdateBillingAddressErrorDto })),
      },
    ],
  },
  {
    method: 'get',
    path: '/reservation/v2/:reservationId/breakfast/selection',
    alias: 'getBreakfastSelection',
    requestFormat: 'json',
    parameters: [
      {
        name: 'reservationId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OkResponseDto.and(z.object({ data: BreakfastSelectionDto })),
    errors: [
      {
        status: 403,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/reservation/v2/:reservationId/breakfast/selection',
    alias: 'patchBreakfastSelection',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BreakfastSelectionDto,
      },
      {
        name: 'reservationId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OkResponseDto,
    errors: [
      {
        status: 400,
        schema: z.void(),
      },
      {
        status: 403,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/reservation/v2/:reservationId/cancel',
    alias: 'cancelReservation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CancelReservationRequestDto,
      },
      {
        name: 'reservationId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OkResponseDto,
    errors: [
      {
        status: 400,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Only IBE and direct reservations that are not InHouse yet can be cancelled`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/reservation/v2/:reservationId/document-upload-url',
    alias: 'getDocumentUploadUrl',
    requestFormat: 'json',
    parameters: [
      {
        name: 'reservationId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'post',
    path: '/reservation/v2/:reservationId/documents',
    alias: 'postDocument',
    requestFormat: 'json',
    parameters: [
      {
        name: 'reservationId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'post',
    path: '/reservation/v2/:reservationId/feedback/additional-info',
    alias: 'postAdditionalInfo',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ additionalInfo: z.string() }),
      },
      {
        name: 'reservationId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OkResponseDto,
  },
  {
    method: 'post',
    path: '/reservation/v2/:reservationId/feedback/feedback',
    alias: 'postFeedback',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PostFeedbackDto,
      },
      {
        name: 'reservationId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OkResponseDto,
  },
  {
    method: 'post',
    path: '/reservation/v2/:reservationId/feedback/nps-score',
    alias: 'postNpsScore',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ score: z.number().gte(0).lte(10) }),
      },
      {
        name: 'reservationId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OkResponseDto,
  },
  {
    method: 'get',
    path: '/reservation/v2/:reservationId/time-options',
    alias: 'getTimeOptions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'reservationId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OkResponseDto.and(z.object({ data: TimeOptionsDto })),
    errors: [
      {
        status: 403,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/reservation/v2/authorize-via-code',
    alias: 'getAuthorizationViaCode',
    description: `Gets a reservation key by a combination of reservation ID / external ID and the main guests´ last name`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AuthorizeViaCodeRequestDto,
      },
    ],
    response: OkResponseDto.and(z.object({ data: AuthorizeViaCodeResponseDto })),
    errors: [
      {
        status: 400,
        schema: ErrorResponseDto.and(z.object({ message: AuthorizeViaCodeErrorDto })),
      },
    ],
  },
  {
    method: 'get',
    path: '/reservation/v2/shared/:reservationId/id-verification-info',
    alias: 'getIdVerificationInfo',
    requestFormat: 'json',
    parameters: [
      {
        name: 'reservationId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OkResponseDto.and(z.object({ data: GetIdVerificationInfoResponseDto })),
  },
]);

export const createReservationApiClient = (baseUrl: string, options?: ZodiosOptions) => {
  return new Zodios(baseUrl, endpoints, options);
};
